import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Eye, EyeOff } from 'lucide-react';
import Logo from '../components/Logo';

const BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function BrandedLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [slugNotFound, setSlugNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.publicTenantBySlug(slug)
      .then(data => setTenantInfo(data))
      .catch(() => setSlugNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Enter email & password'); return; }
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err?.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = tenantInfo?.brand_logo_url
    ? `${BASE}${tenantInfo.brand_logo_url}`
    : null;

  return (
    <div className="min-h-screen bg-light-gradient flex items-center justify-center p-6" data-testid="branded-login-page">
      <div className="w-full max-w-md animate-fade-in">
        {/* Brand / Logo */}
        <div className="flex flex-col items-center mb-8">
          {logoSrc && !slugNotFound ? (
            <img
              src={logoSrc}
              alt={tenantInfo?.business_name || 'Logo'}
              className="w-24 h-24 object-contain rounded-2xl mb-4 shadow-lg"
              data-testid="branded-login-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <Logo size="xl" theme="light" className="mb-6" />
          )}
          {slugNotFound && (
            <p className="text-amber-600 text-xs mb-2 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200" data-testid="branded-login-slug-notice">
              Workspace not found — signing in as any tenant
            </p>
          )}
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" data-testid="branded-login-title">
            {tenantInfo?.business_name ? `Sign in to ${tenantInfo.business_name}` : 'Welcome back'}
          </h1>
          <p className="text-gray-500 mt-2">
            {tenantInfo?.business_name ? 'Enter your credentials to continue.' : 'Sign in to your workspace.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-5">
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="branded-login-error">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="branded-login-email" className="block text-gray-900 text-sm font-bold mb-2 tracking-wide">Email</label>
            <input
              id="branded-login-email"
              data-testid="branded-login-email-input"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 text-base placeholder:text-gray-400 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none transition-all"
            />
          </div>
          <div>
            <label htmlFor="branded-login-password" className="block text-gray-900 text-sm font-bold mb-2 tracking-wide">Password</label>
            <div className="relative">
              <input
                id="branded-login-password"
                data-testid="branded-login-password-input"
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full h-14 px-4 pr-12 rounded-xl border border-gray-200 bg-white text-gray-900 text-base placeholder:text-gray-400 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                data-testid="branded-login-toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="branded-login-submit-btn"
            className="w-full h-14 rounded-xl bg-gradient-to-r from-brand-purple via-brand-purple-soft to-purple-700 text-white font-bold text-base shadow-accent hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
            ) : 'Sign in'}
          </button>
        </form>
        <p className="text-center mt-6 text-gray-500 text-sm">
          No workspace yet?{' '}
          <Link to="/signup" data-testid="branded-go-signup-link" className="text-brand-purple font-bold hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
